// Pure scale math for the chart library (no DOM, unit-tested).
// Hand-rolled instead of d3-scale so the library carries zero chart deps
// (ADR 0131) and the vocabulary stays exactly as small as we need.
//
// The tick and day-band helpers (linear, spaced and time ticks, day starts)
// are the page library's (`@neomaris/controls/core`, cloud ADR 0393 §7.1,
// TOM-665): a page's ticks and bands and this library's charts read one copy.

export type Scale = (v: number) => number;

/** Linear domain→range map. A degenerate domain maps everything to mid-range. */
export function linearScale(d0: number, d1: number, r0: number, r1: number): Scale {
  const span = d1 - d0;
  if (span === 0 || !Number.isFinite(span)) {
    const mid = (r0 + r1) / 2;
    return () => mid;
  }
  const k = (r1 - r0) / span;
  return (v: number) => r0 + (v - d0) * k;
}

/** Standard 1-2-5 tick step for a span and a target tick count. */
export function niceStep(span: number, target: number): number {
  const raw = Math.abs(span) / Math.max(1, target);
  if (raw === 0 || !Number.isFinite(raw)) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const factor = norm < 1.5 ? 1 : norm < 3.5 ? 2 : norm < 7.5 ? 5 : 10;
  return factor * mag;
}

/**
 * Pad a value domain for plotting. Guards the flat-series case (a constant
 * reading still gets a visible band) and can pin the domain to zero for
 * magnitude charts.
 */
export function padDomain(
  min: number,
  max: number,
  frac = 0.08,
  opts: { zero?: boolean } = {}
): [number, number] {
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  if (lo === hi) {
    const bump = Math.max(Math.abs(lo) * 0.1, 1);
    lo -= bump;
    hi += bump;
  }
  const pad = (hi - lo) * frac;
  lo -= pad;
  hi += pad;
  if (opts.zero === true) {
    if (lo > 0) lo = 0;
    if (hi < 0) hi = 0;
  }
  return [lo, hi];
}
